import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GameRoomLayout } from '../components/game/GameRoomLayout';
import { PlayerPanel } from '../components/game/PlayerPanel';
import { CharacterCard } from '../components/game/CharacterCard';
import { EndTurnButton } from '../components/game/EndTurnButton';
import { ConnectionStatusIndicator, ConnectionStatus } from '../components/game/ConnectionStatusIndicator';
import { useToast } from '../components/ui/ToastContainer';
import { GameState, ActiveEffect } from '../types';
import { StatsUpdate } from '../components/game/StatsEditor';
import api from '../services/api';
import websocketService, { ConnectionState } from '../services/websocket';
import { MessageHandler } from '../services/messageHandler';

/**
 * GameRoomPage Component
 * 
 * The main game room interface that orchestrates:
 * - Fetching initial game state on mount
 * - Setting up WebSocket message handlers for real-time updates
 * - Managing selected player state (default to current user)
 * - Handling stats update API calls
 * 
 * Requirements: 1.3, 5.4, 5.5
 */
const GameRoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const isRejoin = searchParams.get('rejoin') === 'true';

  // Game state
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [isEndingTurn, setIsEndingTurn] = useState(false);

  // Track stats that were recently updated for visual feedback
  const [recentlyUpdatedPlayerId, setRecentlyUpdatedPlayerId] = useState<string | null>(null);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Convert room data to game state format
  const roomToGameState = useCallback((room: any): GameState => {
    return {
      roomId: room.id || room.roomId,
      players: room.players,
      currentTurnPlayerId: room.currentTurnPlayerId || '',
      turnOrder: room.turnOrder || [],
      gameStatus: room.state,
    };
  }, []);

  // Fetch initial game state
  const fetchGameState = useCallback(async () => {
    if (!roomId) return;

    try {
      const response = await api.get(`/rooms/${roomId}`);
      const room = response.data;
      const newGameState = roomToGameState(room);
      setGameState(newGameState);
      setError(null);

      // Default to current user's character (Requirement 1.3)
      if (user) {
        setSelectedPlayerId(prev => prev ?? user.id);
      }
    } catch (err) {
      console.error('Failed to fetch game state:', err);
      setError('Failed to load game state');
    } finally {
      setIsLoading(false);
    }
  }, [roomId, user, roomToGameState]);

  // Re-register as a connected player. Used when rejoining an in-progress
  // game so the backend restores our state and broadcasts player_reconnected.
  const rejoinRoom = useCallback(async () => {
    if (!roomId) return;
    try {
      await api.post(`/rooms/${roomId}/join`, {});
    } catch (err: any) {
      // "Already in this room" just means we never lost our seat — not an error
      const errorMsg = err.response?.data?.error || err.response?.data?.message || '';
      if (!errorMsg.includes('already in this room')) {
        console.error('Failed to rejoin room:', err);
      }
    }
  }, [roomId]);

  // Set up WebSocket connection and message handlers
  useEffect(() => {
    if (!roomId || !user) {
      navigate('/rooms');
      return;
    }

    // Set up message handler with callbacks for real-time updates
    const messageHandler = new MessageHandler({
      onTurnChanged: ({ room }) => {
        console.log('Turn changed - updating game state');
        setGameState(roomToGameState(room));
      },
      onStatsUpdated: ({ room }) => {
        console.log('Stats updated via ability - updating game state');
        setGameState(roomToGameState(room));
      },
      onPlayerEliminated: ({ room, playerId }) => {
        console.log('Player eliminated:', playerId);
        setGameState(roomToGameState(room));
        showToast('A player has been eliminated!', 'warning');
      },
      onPlayerStatsUpdated: ({ playerId, updates }) => {
        // Only update the specific player's stats, not the entire game state
        // This prevents unnecessary re-renders and keeps the UI snappy
        setGameState(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            players: prev.players.map(p =>
              p.userId === playerId
                ? { ...p, stats: { ...p.stats, ...updates } }
                : p
            ),
          };
        });

        // Visual feedback for stat changes from other players (Requirement 5.3)
        // Skip highlight for own updates since we already have optimistic UI
        if (playerId !== user?.id) {
          setRecentlyUpdatedPlayerId(playerId);

          if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
          }
          updateTimeoutRef.current = setTimeout(() => {
            setRecentlyUpdatedPlayerId(null);
          }, 1500);
        }
      },
      onPlayerReconnected: ({ room, player }) => {
        console.log('Player reconnected:', player.username);
        setGameState(roomToGameState(room));
        if (player.userId !== user.id) {
          showToast(`${player.username} has reconnected!`, 'info');
        }
      },
      onPlayerLeft: ({ room, leftPlayer, isTemporaryDisconnect }) => {
        console.log('Player left:', leftPlayer?.username);
        setGameState(roomToGameState(room));
        const username = leftPlayer?.username || 'A player';
        if (isTemporaryDisconnect) {
          showToast(`${username} has disconnected and may reconnect`, 'info');
        } else {
          showToast(`${username} has left the game`, 'warning');
        }
      },
      onGameStarted: ({ room }) => {
        console.log('Game started');
        setGameState(roomToGameState(room));
      },
    });

    websocketService.addMessageHandler(messageHandler.handleMessage);

    // Track connection status via service events; on reconnect, re-register
    // with the room and refetch state to make sure we did not miss updates.
    const handleStatusChange = (state: ConnectionState, info?: { isReconnect?: boolean }) => {
      if (state === 'connected') {
        setConnectionStatus('connected');
        if (info?.isReconnect) {
          showToast('Connection restored', 'info');
          rejoinRoom().then(fetchGameState);
        }
      } else if (state === 'connecting' || state === 'reconnecting') {
        setConnectionStatus('reconnecting');
      } else {
        setConnectionStatus('disconnected');
      }
    };
    websocketService.addStatusListener(handleStatusChange);

    // Safe to call unconditionally — no-op when already connected to this room
    websocketService.connect(roomId, user.id);

    // Initial load. When rejoining an in-progress game, re-register first so
    // the backend flips our connectionStatus back to 'connected'.
    const initialize = async () => {
      if (isRejoin) {
        await rejoinRoom();
      }
      await fetchGameState();
    };
    initialize();

    return () => {
      websocketService.removeMessageHandler(messageHandler.handleMessage);
      websocketService.removeStatusListener(handleStatusChange);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [roomId, user, navigate, roomToGameState, showToast, fetchGameState, rejoinRoom, isRejoin]);

  // Handle player selection from PlayerPanel
  const handlePlayerSelect = useCallback((playerId: string) => {
    setSelectedPlayerId(playerId);
  }, []);

  // Handle stats update (Requirement 4.4)
  const handleStatsUpdate = useCallback(async (updates: StatsUpdate) => {
    if (!roomId || !user || !gameState) {
      throw new Error('Cannot update stats: missing required data');
    }

    const currentPlayer = gameState.players.find(p => p.userId === user.id);
    if (!currentPlayer) {
      throw new Error('Cannot update stats: player not found');
    }

    // Store previous stats for rollback (capture before optimistic update)
    const previousStats = { ...currentPlayer.stats };

    // Optimistic UI update - immediate feedback
    setGameState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        players: prev.players.map(p => 
          p.userId === user.id 
            ? { ...p, stats: { ...p.stats, ...updates } }
            : p
        ),
      };
    });

    try {
      // Fire and forget - don't wait for response since we have optimistic update
      // WebSocket will sync the final state
      api.post(`/rooms/${roomId}/update-stats`, { updates }).catch((err: any) => {
        console.error('Failed to update stats:', err);
        
        // Rollback optimistic update on error
        setGameState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            players: prev.players.map(p => 
              p.userId === user.id 
                ? { ...p, stats: previousStats }
                : p
            ),
          };
        });
        
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to update stats';
        showToast(errorMessage, 'error');
      });
    } catch (err: any) {
      // Immediate error (network failure, etc.)
      console.error('Failed to send stats update:', err);
      
      // Rollback optimistic update
      setGameState(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          players: prev.players.map(p => 
            p.userId === user.id 
              ? { ...p, stats: previousStats }
              : p
          ),
        };
      });
      
      showToast('Failed to update stats', 'error');
      throw err;
    }
  }, [roomId, user, gameState, showToast]);

  // Handle effect add
  const handleEffectAdd = useCallback((effect: ActiveEffect) => {
    if (!user || !gameState) return;
    
    const currentPlayer = gameState.players.find(p => p.userId === user.id);
    if (!currentPlayer) return;
    
    const newEffects = [...currentPlayer.stats.activeEffects, effect];
    handleStatsUpdate({ activeEffects: newEffects } as any);
  }, [user, gameState, handleStatsUpdate]);

  // Handle effect remove
  const handleEffectRemove = useCallback((index: number) => {
    if (!user || !gameState) return;
    
    const currentPlayer = gameState.players.find(p => p.userId === user.id);
    if (!currentPlayer) return;
    
    const newEffects = currentPlayer.stats.activeEffects.filter((_, i) => i !== index);
    handleStatsUpdate({ activeEffects: newEffects } as any);
  }, [user, gameState, handleStatsUpdate]);

  // Handle effect duration update
  const handleEffectDurationUpdate = useCallback((index: number, duration: number) => {
    if (!user || !gameState) return;
    
    const currentPlayer = gameState.players.find(p => p.userId === user.id);
    if (!currentPlayer) return;
    
    const newEffects = currentPlayer.stats.activeEffects.map((effect, i) => 
      i === index ? { ...effect, duration } : effect
    );
    handleStatsUpdate({ activeEffects: newEffects } as any);
  }, [user, gameState, handleStatsUpdate]);

  // Handle ability use
  const handleAbilityUse = useCallback((abilityId: string) => {
    if (!user || !gameState) return;
    
    const currentPlayer = gameState.players.find(p => p.userId === user.id);
    if (!currentPlayer) return;
    
    const currentUsages = currentPlayer.stats.abilityUsages || {};
    const currentUses = currentUsages[abilityId];
    
    if (currentUses !== undefined && currentUses > 0) {
      const newUsages = { ...currentUsages, [abilityId]: currentUses - 1 };
      handleStatsUpdate({ abilityUsages: newUsages } as any);
    }
  }, [user, gameState, handleStatsUpdate]);

  // Handle ending the current turn
  const handleEndTurn = useCallback(async () => {
    if (!roomId) return;

    setIsEndingTurn(true);
    try {
      // The turn_changed broadcast updates the game state for everyone,
      // including this client.
      await api.post(`/rooms/${roomId}/end-turn`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to end turn';
      showToast(errorMessage, 'error');
    } finally {
      setIsEndingTurn(false);
    }
  }, [roomId, showToast]);

  // Handle leaving the game. Disconnecting the WebSocket lets the backend
  // mark us as disconnected so we can rejoin the in-progress game later.
  const handleLeaveGame = useCallback(() => {
    websocketService.disconnect();
    navigate('/rooms');
  }, [navigate]);

  // Handle WebSocket retry — reconnect keeps message handlers registered
  // and resets the backoff counter
  const handleRetryConnection = useCallback(() => {
    setConnectionStatus('reconnecting');
    websocketService.reconnect();
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading game room...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !gameState || !user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Game not found'}</p>
          <button
            onClick={() => navigate('/rooms')}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Back to Rooms
          </button>
        </div>
      </div>
    );
  }

  // Find selected player and current user
  const selectedPlayer = gameState.players.find(p => p.userId === selectedPlayerId) ||
                         gameState.players.find(p => p.userId === user.id);
  const isOwnCharacter = selectedPlayer?.userId === user.id;
  const isCurrentTurn = selectedPlayer?.userId === gameState.currentTurnPlayerId;
  const isOwnTurn = gameState.currentTurnPlayerId === user.id;
  const currentTurnPlayer = gameState.players.find(p => p.userId === gameState.currentTurnPlayerId);

  // Game not in progress state
  if (gameState.gameStatus !== 'in-progress') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">
            {gameState.gameStatus === 'waiting' 
              ? 'Game has not started yet. Please wait in the lobby.'
              : 'Game has ended.'}
          </p>
          <button
            onClick={() => {
              if (gameState.gameStatus === 'waiting') {
                // Keep the WebSocket open — the lobby reuses the connection
                navigate(`/rooms/${roomId}/lobby`);
              } else {
                websocketService.disconnect();
                navigate('/rooms');
              }
            }}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
          >
            {gameState.gameStatus === 'waiting' ? 'Go to Lobby' : 'Back to Rooms'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <GameRoomLayout
        header={
          <div className="flex items-center justify-between gap-4 px-6 py-4">
            <div className="flex items-center gap-4 min-w-0">
              <h1 className="text-xl font-bold text-white whitespace-nowrap">
                Game Room - {roomId?.slice(0, 8)}
              </h1>
              <div
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border truncate ${
                  isOwnTurn
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-gray-700/50 text-gray-300 border-gray-600'
                }`}
                data-testid="turn-banner"
              >
                {isOwnTurn
                  ? '🎲 Your Turn'
                  : `Waiting for ${currentTurnPlayer?.username || 'next player'}...`}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-40">
                <EndTurnButton
                  isCurrentPlayerTurn={isOwnTurn}
                  onEndTurn={handleEndTurn}
                  disabled={isEndingTurn}
                />
              </div>
              <button
                onClick={handleLeaveGame}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Leave Game
              </button>
            </div>
          </div>
        }
        leftPanel={
          <PlayerPanel
            players={gameState.players}
            currentUserId={user.id}
            currentTurnPlayerId={gameState.currentTurnPlayerId}
            selectedPlayerId={selectedPlayerId}
            onPlayerSelect={handlePlayerSelect}
          />
        }
        mainContent={
          selectedPlayer ? (
            <div className={`transition-all duration-300 ${
              recentlyUpdatedPlayerId === selectedPlayer.userId 
                ? 'stat-update-highlight ring-2 ring-primary-500 ring-opacity-50' 
                : ''
            }`}>
              <CharacterCard
                player={selectedPlayer}
                isOwnCharacter={isOwnCharacter}
                isCurrentTurn={isCurrentTurn}
                onStatsUpdate={isOwnCharacter ? handleStatsUpdate : undefined}
                onEffectAdd={isOwnCharacter ? handleEffectAdd : undefined}
                onEffectRemove={isOwnCharacter ? handleEffectRemove : undefined}
                onEffectDurationUpdate={isOwnCharacter ? handleEffectDurationUpdate : undefined}
                onAbilityUse={isOwnCharacter ? handleAbilityUse : undefined}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400">Select a player to view their character</p>
            </div>
          )
        }
      />
      
      {/* Connection Status Indicator (Requirements 10.3, 10.4) */}
      <ConnectionStatusIndicator
        status={connectionStatus}
        onRetry={handleRetryConnection}
        position="bottom-right"
      />
    </>
  );
};

export default GameRoomPage;
